/**
 * Static Before / After charting cards for the "All" visual overview (PRD.166).
 *
 * Renders one split-thumbnail card per saved charting record (newest first).
 * Each card is left/right (Before / After), visually distinct from raw image
 * thumbnails, and consistent in sizing with the surrounding grid.
 *
 * These are static preview cards — no interactive slider.
 * Clicking opens the focused Before / After workspace for that pair.
 */

import { useMemo } from 'react';
import { usePowerSyncQuery } from '../../../adapters/powersync';
import {
  qEncounterVisualCharts,
  qPatientFilesByEncounter,
  type EncounterVisualChartRow,
  type PatientFileRow,
} from '@helios/data-access';

interface Props {
  patientId: string;
  encounterId: string;
  onOpen?: (chartId: string) => void;
}

export function SavedBeforeAfterPreview({ patientId, encounterId, onOpen }: Props) {
  const { data: chartRows = [] } = usePowerSyncQuery<EncounterVisualChartRow>(
    qEncounterVisualCharts(encounterId),
  );
  const { data: fileRows = [] } = usePowerSyncQuery<PatientFileRow>(
    qPatientFilesByEncounter(patientId, encounterId),
  );

  const byId = useMemo(() => {
    const m = new Map<string, PatientFileRow>();
    for (const f of fileRows) m.set(f.id, f);
    return m;
  }, [fileRows]);

  const charts = chartRows.filter((c) => c.before_file_id || c.after_file_id);

  if (charts.length === 0) return null;

  return (
    <>
      {charts.map((chart) => {
        const beforeUrl = chart.before_file_id
          ? (byId.get(chart.before_file_id)?.url ?? null)
          : null;
        const afterUrl = chart.after_file_id ? (byId.get(chart.after_file_id)?.url ?? null) : null;

        return (
          <ChartingCard
            key={chart.id}
            beforeUrl={beforeUrl}
            afterUrl={afterUrl}
            onClick={onOpen ? () => onOpen(chart.id) : undefined}
          />
        );
      })}
    </>
  );
}

function ChartingCard({
  beforeUrl,
  afterUrl,
  onClick,
}: {
  beforeUrl: string | null;
  afterUrl: string | null;
  onClick?: () => void;
}) {
  return (
    <button
      onClick={onClick}
      disabled={!onClick}
      title="Open Before / After"
      style={{
        position: 'relative',
        aspectRatio: '1',
        borderRadius: 'var(--radius-md)',
        overflow: 'hidden',
        border: '1px solid var(--border)',
        background: 'var(--bg-surface-2)',
        cursor: onClick ? 'pointer' : 'default',
        padding: 0,
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        width: '100%',
      }}
    >
      <Half url={beforeUrl} fallback="B" />
      <div
        style={{
          position: 'absolute',
          top: 0,
          bottom: 0,
          left: '50%',
          width: 2,
          background: 'rgba(255,255,255,0.7)',
          zIndex: 2,
        }}
      />
      <Half url={afterUrl} fallback="A" />
      <span
        style={{
          position: 'absolute',
          bottom: 4,
          left: '50%',
          transform: 'translateX(-50%)',
          padding: '1px 8px',
          borderRadius: 'var(--radius-sm)',
          background: 'rgba(0,0,0,0.6)',
          color: '#fff',
          fontSize: 9,
          fontWeight: 600,
          letterSpacing: '0.3px',
          textTransform: 'uppercase',
          whiteSpace: 'nowrap',
          zIndex: 3,
        }}
      >
        Before / After
      </span>
    </button>
  );
}

function Half({ url, fallback }: { url: string | null; fallback: string }) {
  if (url) {
    return (
      <img
        src={url}
        alt={fallback}
        style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
      />
    );
  }
  return (
    <div
      style={{
        width: '100%',
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: 'var(--text-muted)',
        fontSize: 'var(--font-size-sm)',
        fontWeight: 600,
      }}
    >
      {fallback}
    </div>
  );
}
